import {
  STATUS_LABELS,
  type DocumentStatus,
  type FlowCapabilities,
  type FlowActionKind,
  type ResponsibilityRole,
} from './types';

// Presentación pura de las acciones del flujo: etiquetas funcionales (nunca el slug
// crudo, UX-COPY-01), marca de acción destructiva y traducción de la razón de bloqueo.

const STATUS_MOVE: Partial<Record<DocumentStatus, string>> = {
  borrador: 'elaboración',
  en_revision: 'revisión',
  en_aprobacion: 'aprobación',
};

/** Nombre de la etapa de destino tal como la ve el usuario (títulos y mensajes). */
export function stageName(status: DocumentStatus): string {
  return STATUS_MOVE[status] ?? STATUS_LABELS[status].toLowerCase();
}

/** Texto del botón de una acción, según su destino cuando aplica. */
export function actionLabel(action: FlowActionKind, toStatus?: DocumentStatus): string {
  switch (action) {
    case 'transition':
      return `Enviar a ${STATUS_MOVE[toStatus ?? 'borrador'] ?? 'la siguiente etapa'}`;
    case 'return':
      return `Devolver a ${STATUS_MOVE[toStatus ?? 'borrador'] ?? 'una etapa previa'}`;
    case 'approve':
      return 'Firmar y aprobar';
    case 'review-conformity':
      return 'Dar conformidad';
    case 'close-approval':
      return 'Cerrar la aprobación con las firmas registradas';
    case 'edit-online':
      return 'Editar en línea';
    case 'decommission':
      return 'Dar de baja';
    case 'start-modification':
      return 'Iniciar modificación';
    default:
      return 'Acción';
  }
}

/** Acciones que piden confirmación antes de ejecutarse (UX-FB-01). */
export function isDestructive(action: FlowActionKind): boolean {
  return action === 'return' || action === 'decommission';
}

const BLOCKED: Record<string, string> = {
  missing_permission: 'No tienes permiso para esta acción',
  type_restricted: 'Este tipo de documento no permite esta acción a tu rol',
  requires_change_log: 'Falta el control de cambios de esta versión',
  requires_lms_file:
    'El documento no tiene archivo en el LMS: vincúlalo desde la ficha, en «Reserva y archivo»',
  locked: 'El documento está bloqueado en esta etapa',
  quality_only: 'Esta etapa no tiene responsables asignados: la opera Calidad',
  org_unavailable: 'No se pudo consultar el organigrama; intenta de nuevo en unos minutos',
  already_conformed: 'Ya diste tu conformidad',
  already_signed: 'Ya firmaste esta versión',
  slot_covered: 'Otra persona de tu puesto ya actuó por el puesto en esta ronda',
  no_sign_permission: 'Tu rol no tiene permiso de firma',
};

const ROLE_PLURAL: Record<ResponsibilityRole, string> = {
  elaborador: 'elaboradores',
  revisor: 'revisores',
  aprobador: 'aprobadores',
  capacitador: 'capacitadores',
};

/** Contexto de la etapa para explicar el bloqueo (G2-8). */
export interface BlockedContext {
  action?: FlowActionKind;
  toStatus?: DocumentStatus;
  role?: ResponsibilityRole;
  /** Nombres de quien falta (conformidades pendientes). */
  pending?: string[];
}

/** La acción en minúscula, como complemento de "pueden …". */
function verbOf(action: FlowActionKind | undefined, toStatus?: DocumentStatus): string | null {
  switch (action) {
    case 'transition':
    case 'return':
      return actionLabel(action, toStatus).toLowerCase();
    case 'approve':
      return 'firmar';
    case 'review-conformity':
      return 'dar conformidad';
    default:
      return null;
  }
}

/** Texto de por qué una acción está deshabilitada; vacío si no hay razón. */
export function blockedReasonLabel(reason: string | undefined, ctx: BlockedContext = {}): string {
  if (reason === undefined) {
    return '';
  }
  if (reason === 'not_assigned') {
    const verb = verbOf(ctx.action, ctx.toStatus);
    if (ctx.role === undefined || verb === null) {
      return 'Solo los responsables asignados pueden operar esta etapa';
    }
    return `Solo los ${ROLE_PLURAL[ctx.role]} asignados pueden ${verb}`;
  }
  if (reason === 'pending_conformities') {
    return ctx.pending !== undefined && ctx.pending.length > 0
      ? `Faltan conformidades: ${ctx.pending.join(', ')}`
      : 'Faltan conformidades de revisores asignados';
  }
  return BLOCKED[reason] ?? '';
}

const SEALED_RETURN_WARNING =
  'Las firmas de esta aprobación quedan sin efecto y se anula su fecha de vigencia: habrá que volver a aprobarla. Si la capacitación todavía no se envió al LMS, se cancela; si ya se envió, las lecturas siguen asignadas y la nueva aprobación actualiza su fecha límite.';
const PARTIAL_RETURN_WARNING =
  'Las firmas ya registradas en esta aprobación quedan sin efecto: habrá que volver a firmarla.';
const REVIEW_RETURN_WARNING =
  'Las conformidades ya registradas en esta revisión quedan sin efecto: habrá que volver a darlas.';

/** Lo que se pierde al devolver, si algo. devolucion.service deja sin efecto todos los
 *  actos de la ronda (firmas o conformidades, `actCount`, tengan o no cupo vigente); con la
 *  aprobación sellada, además la vigencia y la capacitación sin enviar. Sin actos que
 *  perder, no hay aviso. */
export function returnWarning(
  caps: Pick<FlowCapabilities, 'currentStatus' | 'lockedAt' | 'stage'>,
): string | undefined {
  if (caps.currentStatus === 'en_aprobacion' && caps.lockedAt !== null) {
    return SEALED_RETURN_WARNING;
  }
  if (caps.stage === null || caps.stage.actCount === 0) {
    return undefined;
  }
  if (caps.currentStatus === 'en_aprobacion') {
    return PARTIAL_RETURN_WARNING;
  }
  return caps.currentStatus === 'en_revision' ? REVIEW_RETURN_WARNING : undefined;
}
